import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import CompanyContext from '../../../auth/CompanyContext';
import Loading from '../../Loading/Loading';

const PortalAccountSummary = (props) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(props.companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const { callback, onWidgetLoad, onAddCallback } = props;

    const getValues = useCallback((width) => {
        let col1 = 0, col2 = 0, graphHeight = 0;
        switch (width) {
            case 4:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 6:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 12:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            default:
        }
        return { col1, col2, graphHeight };
    }, []);

    const initialValues = getValues(props.width);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [col1, setCol1] = useState(initialValues.col1);
    const [col2, setCol2] = useState(initialValues.col2);
    const [graphHeight, setGraphHeight] = useState(initialValues.graphHeight);
    const [styleSettings, setStyleSettings] = useState({});
    const [date, setDate] = useState('');
    const [beginDate, setBeginDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const refreshRef = useRef(() => { });
    const styleSettingsRef = useRef({});

    const toNumber = useCallback((val) => {
        const parsed = Number(val);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const getDateCode = useCallback((dateString) => {
        switch (dateString) {
            case 'Month to Date':
                return 'mtd';
            case 'Quarter to Date':
                return 'qtd';
            case 'Previous Quarter':
                return 'pqtr';
            case 'Previous 3 Months':
                return '3mo';
            case 'Previous 6 Months':
                return '6mo';
            case 'Year to Date':
                return 'ytd';
            case 'Previous Year':
                return 'pyr';
            case 'Last 1 Year':
                return 'l1y';
            case 'Last 3 Years':
                return 'l3y';
            case 'Last 5 Years':
                return 'l5y';
            case 'Last 7 Years':
                return 'l7y';
            case 'Last 10 Years':
                return 'l10y';
            case 'Last 15 Years':
                return 'l15y';
            case 'Last 20 Years':
                return 'l20y';
            case 'Last 25 Years':
                return 'l25y';
            case 'Inception to Date':
                return 'itd';
            case 'Hidden':
                return '';
            default:
                return '';
        }
    }, []);

    const getStyleSettings = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return;

        const styleNames = ['acctPeriod1', 'chartDefaultFees'];
        const params = new URLSearchParams();
        styleNames.forEach(name => params.append('styleNames[]', name));

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/getStyleSettings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            const nextStyleSettings = response.styleSettings || {};
            styleSettingsRef.current = nextStyleSettings;
            setStyleSettings(nextStyleSettings);
        } catch (err) {
            console.error(err);
            styleSettingsRef.current = {};
            setStyleSettings({});
        }
    }, [props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getPerfDates = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return '';

        const currentStyleSettings = styleSettingsRef.current || {};
        const payload = {
            port: '',
            perfType: currentStyleSettings.chartDefaultFees || 'Net',
            email: props.user.user,
            household: props.selectedHousehold
        };

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/perfIRRDates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                },
                body: JSON.stringify(payload)
            });
            const response = await results.json();
            if (response.code === 200 && response.data && response.data.length > 0) {
                return response.data[0].fDate;
            }
            return '';
        } catch (err) {
            console.log(err);
            return '';
        }
    }, [props.user.user, props.selectedHousehold, props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getTransactions = useCallback(async (queryDateValue) => {
        if (!hasCompanyPortalDatabase) {
            setData(null);
            setLoading(false);
            return;
        }

        setLoading(true);

        const currentStyleSettings = styleSettingsRef.current || {};
        const queryDate = queryDateValue ? new Date(queryDateValue).toISOString() : '';
        const params = new URLSearchParams({
            household: props.selectedHousehold || '',
            date: queryDate,
            fees: currentStyleSettings.chartDefaultFees || 'Net',
            period: getDateCode(currentStyleSettings.acctPeriod1 || '')
        });

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/accountSummarySnapshot?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();

            if (response.code !== 200) {
                setData(null);
                setLoading(false);
                setBeginDate(null);
                setEndDate(null);
                return;
            }

            const rows = response.data || [];
            if (rows.length === 0) {
                setData(null);
                setLoading(false);
                setBeginDate(null);
                setEndDate(null);
                return;
            }

            const beginDateVal = new Date(rows[0].beginDate);
            const endDateVal = new Date(rows[0].endDate);
            const totals = {
                beginMV: 0,
                additionsWithdrawals: 0,
                income: 0,
                realizedGains: 0,
                unrealizedGains: 0,
                fees: 0,
                endMV: 0
            };

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i] || {};
                totals.beginMV += toNumber(row.beginAI) + toNumber(row.beginMV);
                totals.additionsWithdrawals += toNumber(row.additions) + toNumber(row.withdrawals);
                totals.income += toNumber(row.transfersIn) + toNumber(row.transfersOut);
                totals.realizedGains += toNumber(row.realizedGains);
                totals.unrealizedGains += toNumber(row.unrealizedGains);
                totals.fees += toNumber(row.manfees) + toNumber(row.manFeesClient) + toNumber(row.portFees) + toNumber(row.portFeesClient);
                totals.endMV += toNumber(row.endAI) + toNumber(row.endMV);
            }

            if ((currentStyleSettings.chartDefaultFees || 'Net') === 'Gross') {
                totals.additionsWithdrawals += totals.fees;
            }

            setData(totals);
            setLoading(false);
            setBeginDate(beginDateVal);
            setEndDate(endDateVal);
        } catch (err) {
            console.log(err);
            setData(null);
            setLoading(false);
            setBeginDate(null);
            setEndDate(null);
        }
    }, [props.selectedHousehold, props.idToken, companyCtx.company, getDateCode, toNumber, hasCompanyPortalDatabase]);

    const onSubmit = useCallback(async () => {
        const nextDate = await getPerfDates();
        setDate(nextDate);
        getTransactions(nextDate);
    }, [getPerfDates, getTransactions]);

    const runRefresh = useCallback(() => {
        setLoading(true);
        onSubmit();
    }, [onSubmit]);

    useEffect(() => {
        refreshRef.current = runRefresh;
    }, [runRefresh]);

    useEffect(() => {
        let active = true;

        const init = async () => {
            await getStyleSettings();
            if (!active) return;

            callback((width) => {
                if (!active) return;
                const values = getValues(width);
                setGraphHeight(values.graphHeight);
                setCol1(values.col1);
                setCol2(values.col2);
            });

            if (onWidgetLoad) {
                onWidgetLoad();
                onAddCallback(() => {
                    refreshRef.current();
                });
            }
        };

        init();

        return () => {
            active = false;
        };
    }, [hasCompanyPortalDatabase]);

    const formatCurrency = (val) => {
        const parsedVal = toNumber(val);
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parsedVal);
    };

    const renderRow = (label, value, indent = true) => {
        return (
            <tr>
                <td className={indent ? 'acctSummLabelIndent' : 'acctSummLabel'}>
                    <label style={{ fontWeight: 'bold' }}>{label}</label>
                </td>
                <td className='acctSummValue'>
                    <label style={{ fontWeight: 'bold' }}>{formatCurrency((Math.round(toNumber(value) * 100) / 100).toFixed(2))}</label>
                </td>
            </tr>
        );
    };

    void styleSettings;
    void date;
    void col1;
    void col2;

    return (
        <Card className='w-100 m-0'>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h4>Account Summary</h4>
                {props.icon}
            </CardHeader>
            <CardBody className='p-3'>
                {loading && <Loading />}
                {!loading && (
                    <>
                        {data && beginDate && endDate ? (
                            <>
                                <div className='acctSummSnapshotHeader'>
                                    <label>{`${beginDate.toLocaleDateString('en-US', { timeZone: 'UTC' })} to ${endDate.toLocaleDateString('en-US', { timeZone: 'UTC' })}`}</label>
                                </div>
                                <div style={{ minHeight: graphHeight + 'px' }}>
                                    <table className='acctSummSnapshot w-100'>
                                        <tbody>
                                            {renderRow('Beginning Value', data.beginMV, false)}
                                            {renderRow('Additions/Withdrawals', data.additionsWithdrawals)}
                                            {renderRow('Income', data.income)}
                                            {renderRow('Realized Gains', data.realizedGains)}
                                            {renderRow('Unrealized Gains', data.unrealizedGains)}
                                            {(styleSettingsRef.current.chartDefaultFees === 'Net') && renderRow('Total Fees', data.fees)}
                                            {renderRow('Ending Value', data.endMV)}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className='widgetNoData'>No data available</div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default PortalAccountSummary;
